import type { PermitTypeDefinitionResponse } from '@contracts/permitType/PermitTypeDefinitionResponse';
import { AuthenticatedClient } from '@src/common/services/AuthenticatedClient';
import { DocumentsClient } from '@src/documents/services/DocumentsClient';
import type { Document } from '@src/orders/domain/PermitApplication';
import type {
  ExistingPermitTypeDefinition,
  PermitTypeDefinition,
} from '@src/permitTypesManager/PermitTypeDefinition';

export const PermitTypeClient = {
  createType: async (
    data: PermitTypeDefinition,
    file?: File,
  ): Promise<string> => {
    let customDocument: Document | undefined;
    if (file) {
      customDocument = await uploadCustomDocument(data, file);
      data.customDocumentKey = customDocument.key;
    }
    try {
      const { data: response } = await AuthenticatedClient.post(
        '/permitTypes',
        data,
      );
      return response;
    } catch (error) {
      if (file && customDocument) {
        await DocumentsClient.delete(customDocument.documentId);
      }
      throw error;
    }
  },
  getType: async (id: string): Promise<ExistingPermitTypeDefinition> => {
    const { data: response } =
      await AuthenticatedClient.get<PermitTypeDefinitionResponse>(
        `/permitTypes/${id}`,
      );

    return {
      permitTypeId: id,
      jurisdiction: response.jurisdiction,
      jurisdictionUrl: response.jurisdictionUrl,
      type: response.type,
      currency: response.currency,
      name: response.name,
      highPriority: response.isPriority,
      fields: response.fields,
      conditionSheetsIds: response.conditionSheetsIds,
      isSelfIssued: response.isSelfIssued,
      showToClient: response.showToClient,
      instructions: response.instructions,
      showInstructionsToClient: response.showInstructionsToClient,
      unit: response.unit,
      validateExpirationDate: response.validateExpirationDate,
      validateExpirationTime: response.validateExpirationTime,
    };
  },
  editType: async (
    id: string,
    data: PermitTypeDefinition,
    file?: File,
  ): Promise<void> => {
    let customDocument: Document | undefined;
    if (file) {
      customDocument = await uploadCustomDocument(data, file);
      data.customDocumentKey = customDocument.key;
    }
    try {
      const { data: response } = await AuthenticatedClient.patch(
        `/permitTypes/${id}:edit`,
        data,
      );
      return response;
    } catch (error) {
      if (file && customDocument && fileIsNew(file)) {
        await DocumentsClient.delete(customDocument.documentId);
      }
      throw error;
    }
  },
  deactivateType: async (id: string): Promise<void> => {
    await AuthenticatedClient.put<void>(`/permitTypes/${id}:deactivate`);
  },
  reactivateType: async (id: string): Promise<void> => {
    await AuthenticatedClient.put<void>(`/permitTypes/${id}:reactivate`);
  },
};

async function uploadCustomDocument(
  data: PermitTypeDefinition,
  file: File,
): Promise<Document> {
  try {
    return await DocumentsClient.upload({
      file,
      customDocumentInformation: {
        permitName: data.name,
        permitType: data.type,
        permitJurisdiction: data.jurisdiction,
      },
    });
  } catch {
    throw Error();
  }
}

function fileIsNew(file: File): boolean {
  const now = new Date();
  const twoMinutesAgo = new Date(now.getTime() - 2 * 60 * 1000);
  return file.lastModified >= twoMinutesAgo.getTime();
}
